// conventions-allow: file-size — this mFRR API module keeps related query and mutation hooks together under the entity API convention; extracting only this alert formatter would add churn without improving the API contract.
import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from 'api/apiClient/apiClient'
import { queryClient } from 'helpers/queryClient'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import { usePusherSocketIdHeaders } from 'ui/components/PusherContext/PusherContext'
import {
  MFRR_CM_BIDS_QUERY_KEY,
  MFRR_CONFIG_ENDPOINT,
  MFRR_CONFIG_QUERY_KEY,
  MFRR_CREATE_BIDS_ENDPOINT,
  MFRR_DELIVERED_BIDS_ENDPOINT,
  MFRR_DELIVERED_BIDS_QUERY_KEY,
  MFRR_ENABLED_PRODUCTION_UNITS_ENDPOINT,
  MFRR_ENABLED_PRODUCTION_UNITS_QUERY_KEY,
  MFRR_GET_BIDS_ENDPOINT,
  MFRR_MARKET_RESULTS_ENDPOINT,
  MFRR_MARKET_RESULTS_QUERY_KEY,
  MFRR_PUBLISH_BIDS_FOR_DATE_ENDPOINT,
  MFRR_PUBLISHED_BIDS_ENDPOINT,
  MFRR_PUBLISHED_BIDS_QUERY_KEY,
  MFRR_UNPUBLISH_BIDS_ENDPOINT,
  MFRR_UPDATE_BIDS_ENDPOINT,
} from './mfrr.constants'
import type {
  EnabledMfrrProductionUnit,
  GetMfrrBidsParams,
  GetMfrrMarketResultsParams,
  MfrrBidResponse,
  MfrrConfig,
  MfrrMarket,
  MfrrMarketResult,
  PublishMfrrBidsRequest,
  SubmitMfrrBidsRequest,
  SubmitMfrrBidsResponse,
  UnpublishMfrrBidsRequest,
} from './mfrr.types'

function getMfrrMarketLabel(market: MfrrMarket | undefined): string {
  return market === 'eam' ? 'EAM' : 'CM'
}

function getFirstStringFromValue(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }

  if (value && typeof value === 'object') {
    for (const item of Object.values(value)) {
      const nested = getFirstStringFromValue(item)
      if (nested) {
        return nested
      }
    }
  }

  return undefined
}

function getBackendErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const payloads: unknown[] = []

  const body = 'body' in error ? (error as { body?: unknown }).body : undefined
  if (body !== undefined) {
    payloads.push(body)
  }

  const response = 'response' in error ? (error as { response?: unknown }).response : undefined
  if (response && typeof response === 'object') {
    const responseData = 'data' in response ? (response as { data?: unknown }).data : undefined
    if (responseData !== undefined) {
      payloads.push(responseData)
    }
  }

  for (const payload of payloads) {
    const directMessage = getFirstStringFromValue(payload)
    if (directMessage) {
      return directMessage
    }

    if (payload && typeof payload === 'object') {
      const detail = 'detail' in payload ? (payload as { detail?: unknown }).detail : undefined
      const message = 'message' in payload ? (payload as { message?: unknown }).message : undefined
      const detailMessage = getFirstStringFromValue(detail) ?? getFirstStringFromValue(message)
      if (detailMessage) {
        return detailMessage
      }

      const values = Object.values(payload as Record<string, unknown>)
      for (const value of values) {
        const nestedMessage = getFirstStringFromValue(value)
        if (nestedMessage) {
          return nestedMessage
        }
      }
    }
  }

  return undefined
}

function formatMfrrErrorAlert(baseMessage: string, error: unknown): string {
  const backendMessage = getBackendErrorMessage(error)
  return backendMessage ? `${baseMessage} (${backendMessage})` : baseMessage
}

export function useEnabledMfrrProductionUnits(systemId: number | undefined, market: MfrrMarket, enabled?: boolean) {
  const params = { system_id: systemId, market }

  return useQuery({
    queryKey: [MFRR_ENABLED_PRODUCTION_UNITS_QUERY_KEY, params],
    queryFn: async (): Promise<EnabledMfrrProductionUnit[]> => {
      if (systemId === undefined || systemId === null) {
        return []
      }

      return apiClient<EnabledMfrrProductionUnit[]>(MFRR_ENABLED_PRODUCTION_UNITS_ENDPOINT, {
        params,
        ignoreTrailingBackslash: true,
      })
    },
    enabled: enabled ?? !!systemId,
  })
}

export function useMfrrCmBids({ systemId, market, startTime, endTime }: GetMfrrBidsParams) {
  const params = {
    system_id: systemId,
    market,
    start_time: startTime,
    end_time: endTime,
  }

  return useQuery({
    queryKey: [MFRR_CM_BIDS_QUERY_KEY, params],
    queryFn: async (): Promise<MfrrBidResponse[]> => {
      if (systemId === undefined || systemId === null) {
        return []
      }

      return apiClient<MfrrBidResponse[]>(MFRR_GET_BIDS_ENDPOINT, { params })
    },
    enabled: systemId !== undefined,
  })
}

export function useMfrrPublishedBids({ systemId, market, startTime, endTime }: GetMfrrBidsParams) {
  const params = {
    system_id: systemId,
    market,
    start_time: startTime,
    end_time: endTime,
  }

  return useQuery({
    queryKey: [MFRR_PUBLISHED_BIDS_QUERY_KEY, params],
    queryFn: async (): Promise<MfrrBidResponse[]> => {
      if (systemId === undefined || systemId === null) {
        return []
      }

      return apiClient<MfrrBidResponse[]>(MFRR_PUBLISHED_BIDS_ENDPOINT, { params })
    },
    enabled: systemId !== undefined,
  })
}

export function useMfrrDeliveredBids({ systemId, market, startTime, endTime }: GetMfrrBidsParams) {
  const params = {
    system_id: systemId,
    market,
    start_time: startTime,
    end_time: endTime,
  }

  return useQuery({
    queryKey: [MFRR_DELIVERED_BIDS_QUERY_KEY, params],
    queryFn: async (): Promise<MfrrBidResponse[]> => {
      if (systemId === undefined || systemId === null) {
        return []
      }

      return apiClient<MfrrBidResponse[]>(MFRR_DELIVERED_BIDS_ENDPOINT, { params })
    },
    enabled: systemId !== undefined,
  })
}

export function useMfrrMarketResults({ systemId, market, startTime, endTime }: GetMfrrMarketResultsParams) {
  const params = {
    system_id: systemId,
    market,
    start_time: startTime,
    end_time: endTime,
  }

  return useQuery({
    queryKey: [MFRR_MARKET_RESULTS_QUERY_KEY, params],
    queryFn: async (): Promise<MfrrMarketResult[]> => {
      if (systemId === undefined || systemId === null) {
        return []
      }

      return apiClient<MfrrMarketResult[]>(MFRR_MARKET_RESULTS_ENDPOINT, { params })
    },
    enabled: systemId !== undefined,
  })
}

export function useMfrrConfig(systemId: number | undefined) {
  return useQuery({
    queryKey: [MFRR_CONFIG_QUERY_KEY, { system_id: systemId }],
    queryFn: async (): Promise<MfrrConfig | null> => {
      if (systemId === undefined || systemId === null) {
        return null
      }

      return apiClient<MfrrConfig>(MFRR_CONFIG_ENDPOINT, {
        params: { system_id: systemId },
        ignoreTrailingBackslash: true,
      })
    },
    enabled: systemId !== undefined,
  })
}

export function useSubmitMfrrCmBidsMutation() {
  const { t } = useTranslation()
  const socketIdHeaders = usePusherSocketIdHeaders()

  return useMutation({
    mutationFn: async ({ payload }: SubmitMfrrBidsRequest) =>
      apiClient<SubmitMfrrBidsResponse>(MFRR_CREATE_BIDS_ENDPOINT, {
        method: 'POST',
        data: payload,
        headers: socketIdHeaders,
      }),
    onSuccess: (_response, { payload }) => {
      const marketLabel = getMfrrMarketLabel(payload.bids[0]?.market)
      queryClient.invalidateQueries({ queryKey: [MFRR_CM_BIDS_QUERY_KEY] })
      alertStore.success(t('mFRR-{{market}} bids submitted successfully.', { market: marketLabel }))
    },
    onError: (error, { payload }) => {
      const marketLabel = getMfrrMarketLabel(payload.bids[0]?.market)
      const baseMessage = t('Failed to submit mFRR-{{market}} bids.', { market: marketLabel })
      alertStore.error(formatMfrrErrorAlert(baseMessage, error))
    },
  })
}

export function useUpdateMfrrCmBidsMutation() {
  const { t } = useTranslation()
  const socketIdHeaders = usePusherSocketIdHeaders()

  return useMutation({
    mutationFn: async ({ payload }: SubmitMfrrBidsRequest) =>
      apiClient<SubmitMfrrBidsResponse>(MFRR_UPDATE_BIDS_ENDPOINT, {
        method: 'PUT',
        data: payload,
        headers: socketIdHeaders,
      }),
    onSuccess: (_response, { payload }) => {
      const marketLabel = getMfrrMarketLabel(payload.bids[0]?.market)
      queryClient.invalidateQueries({ queryKey: [MFRR_CM_BIDS_QUERY_KEY] })
      alertStore.success(t('mFRR-{{market}} bids updated successfully.', { market: marketLabel }))
    },
    onError: (error, { payload }) => {
      const marketLabel = getMfrrMarketLabel(payload.bids[0]?.market)
      const baseMessage = t('Failed to update mFRR-{{market}} bids.', { market: marketLabel })
      alertStore.error(formatMfrrErrorAlert(baseMessage, error))
    },
  })
}

// conventions-allow: file-size — this mFRR API module keeps the four related mutation hooks and the shared backend-error formatter together; extracting them would add churn without improving the API contract.

export function usePublishMfrrCmBidsMutation() {
  const { t } = useTranslation()
  const socketIdHeaders = usePusherSocketIdHeaders()

  return useMutation({
    mutationFn: async ({ payload }: PublishMfrrBidsRequest) =>
      apiClient<SubmitMfrrBidsResponse>(MFRR_PUBLISH_BIDS_FOR_DATE_ENDPOINT, {
        method: 'POST',
        data: payload,
        headers: socketIdHeaders,
      }),
    onSuccess: (_response, { payload }) => {
      const marketLabel = getMfrrMarketLabel(payload.market)
      queryClient.invalidateQueries({ queryKey: [MFRR_CM_BIDS_QUERY_KEY] })
      alertStore.success(t('mFRR-{{market}} bids published successfully.', { market: marketLabel }))
    },
    onError: (error, { payload }) => {
      const marketLabel = getMfrrMarketLabel(payload.market)
      const baseMessage = t('Failed to publish mFRR-{{market}} bids.', { market: marketLabel })
      alertStore.error(formatMfrrErrorAlert(baseMessage, error))
    },
  })
}

export function useUnpublishMfrrCmBidsMutation() {
  const { t } = useTranslation()
  const socketIdHeaders = usePusherSocketIdHeaders()

  return useMutation({
    mutationFn: async ({ payload }: UnpublishMfrrBidsRequest) =>
      apiClient<MfrrBidResponse[]>(MFRR_UNPUBLISH_BIDS_ENDPOINT, {
        method: 'POST',
        data: payload,
        headers: socketIdHeaders,
      }),
    onSuccess: (_response, { payload }) => {
      const marketLabel = getMfrrMarketLabel(payload.market)
      queryClient.invalidateQueries({ queryKey: [MFRR_CM_BIDS_QUERY_KEY] })
      alertStore.success(t('mFRR-{{market}} bids unlocked successfully.', { market: marketLabel }))
    },
    onError: (error, { payload }) => {
      const marketLabel = getMfrrMarketLabel(payload.market)
      const baseMessage = t('Failed to unlock mFRR-{{market}} bids.', { market: marketLabel })
      alertStore.error(formatMfrrErrorAlert(baseMessage, error))
    },
  })
}
